import React, { type ChangeEvent, Fragment, useCallback, useMemo, useState } from 'react';

import Button from '@atlaskit/button/new';
import { Code } from '@atlaskit/code';
import { cssMap } from '@atlaskit/css';
import DynamicTable from '@atlaskit/dynamic-table';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
	clearAllOverrides,
	getOverridesFromCache,
	setOverrides as setPersistedOverrides,
} from '../../../common/persistance';
import { AddOverride } from '../add-override';
import { DynamicPromptFGInput } from '../dynamic-prompt-fg-input';
import { StudioAppSwitcherToggle } from '../studio-app-switcher-toggle';
import { getRows, TABLE_COLUMN_HEADINGS } from '../utils/templating-utils';

const styles = cssMap({
	toggleTextarea: { paddingBlock: token('space.150') },
	inputInstructionText: {
		marginBottom: token('space.150'),
	},
	descriptionText: {
		marginTop: token('space.150'),
	},
	emptyView: {
		paddingBottom: token('space.200'),
		marginTop: token('space.negative.150'),
	},
	divider: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginTop: token('space.200'),
		marginRight: 0,
		marginBottom: token('space.200'),
		marginLeft: 0,
	},
	inputContainer: {
		width: '100%',
		marginBottom: token('space.150'),
		marginTop: token('space.150'),
	},
	buttonContainer: {
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
		gap: token('space.150'),
	},
	statsigTabContainer: {
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		marginRight: 0,
		marginLeft: 0,
		width: '100%',
	},
	dynamicPromptFGInputContainer: {
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
	},
});

const textAreaStyles = {
	marginBottom: token('space.150'),
	fontFamily: token('font.family.code'),
};

const codeStyles = {
	display: 'inline',
	backgroundColor: token('color.background.accent.gray.subtlest'),
	padding: token('space.025'),
	borderRadius: token('radius.small'),
};

export const StatsigLocalOverridesTab = (): React.JSX.Element => {
	const [overrides, setOverrides] = useState(getOverridesFromCache());
	const [isCopied, setIsCopied] = useState(false);
	const [jsonInput, setJsonInput] = useState(() =>
		JSON.stringify(getOverridesFromCache(), null, 2),
	);
	const [isTextareaCollapsed, setIsTextareaCollapsed] = useState(true);
	const [isJsonValid, setIsJsonValid] = useState(true);

	const updateOverrides = useCallback(() => {
		const newOverrides = getOverridesFromCache();
		setOverrides({ ...newOverrides });
		setJsonInput(JSON.stringify(newOverrides, null, 2));
	}, []);

	const rows = useMemo(() => getRows(overrides, updateOverrides), [overrides, updateOverrides]);

	const resetAll = useCallback(() => {
		if (window.confirm('Are you sure you want to remove all overrides?')) {
			clearAllOverrides();
			updateOverrides();
		}
	}, [updateOverrides]);

	const copy = useCallback(() => {
		const allData = JSON.stringify(overrides, null, 2);
		void navigator.clipboard.writeText(allData).then(() => {
			setIsCopied(true);
			setTimeout(() => setIsCopied(false), 3000);
		});
	}, [overrides]);

	const replaceAll = useCallback(() => {
		if (jsonInput && isJsonValid) {
			try {
				const newOverrides = JSON.parse(jsonInput);
				if (fg('asf-380-fix-bulk-fg-not-updating-localstorage')) {
					clearAllOverrides();
					setPersistedOverrides(newOverrides);
					updateOverrides();
				} else {
					setOverrides(newOverrides);
				}
			} catch {
				alert('Invalid JSON');
			}
		}
	}, [jsonInput, isJsonValid, updateOverrides]);

	const toggleTextarea = useCallback(() => {
		setIsTextareaCollapsed((prev) => !prev);
	}, []);

	const onChangeValidation = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
		const inputValue = event.target.value;
		setJsonInput(inputValue);
		try {
			JSON.parse(inputValue);
			setIsJsonValid(true);
		} catch {
			setIsJsonValid(false);
		}
	}, []);

	return (
		<Box xcss={styles.statsigTabContainer}>
			<Heading size="small" as="h3">
				Statsig local gate overrides
			</Heading>
			<Box xcss={styles.descriptionText}>
				<Text>
					Set local overrides for your Statsig gates below. Rerender your component to see the
					changes.
					<br />
					<Text weight="bold">Note</Text>: Local overrides will only apply to flags checked with the
					util function:{' '}
					<Code
						size={12}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={codeStyles}
					>
						studioFg('GATE_KEY')
					</Code>
					.
					<br />
					<Text weight="bold">Note</Text>: Overrides in SSR stage only work in dev/staging
					environments and Hello.
					<br />
					<Text weight="bold">Tip</Text>: You can also set overrides via the{' '}
					<Anchor
						href="https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/home"
						target="_blank"
						rel="noopener noreferrer"
					>
						Statsig console
					</Anchor>
					.
				</Text>

				<Inline xcss={styles.buttonContainer} testId="button-container">
					<Tooltip
						content="Clear all current feature gates and experiments overrides"
						position="top"
					>
						{(tooltipProps) => (
							<Button
								{...tooltipProps}
								appearance="danger"
								isDisabled={Object.keys(overrides.gates).length === 0}
								onClick={resetAll}
							>
								Remove all
							</Button>
						)}
					</Tooltip>
					<Tooltip
						content="Copy the JSON representation of all current feature gates and experiment overrides"
						position="top"
					>
						{(tooltipProps) => (
							<Button
								{...tooltipProps}
								appearance="primary"
								isDisabled={Object.keys(overrides.gates).length === 0}
								onClick={copy}
							>
								{isCopied ? 'Copied!' : 'Copy all'}
							</Button>
						)}
					</Tooltip>
				</Inline>
			</Box>

			<DynamicTable
				head={TABLE_COLUMN_HEADINGS}
				rows={rows}
				emptyView={
					<Box xcss={styles.emptyView} testId="no-overrides">
						<Text size="small">No overrides currently active</Text>
					</Box>
				}
			/>

			<AddOverride updateOverrides={updateOverrides} />
			<Box xcss={styles.divider} />
			<Heading size="small" as="h4">
				Overrides in JSON format
			</Heading>
			<Box xcss={styles.inputContainer}>
				{!isTextareaCollapsed && (
					<Fragment>
						<Box as="p" xcss={styles.inputInstructionText}>
							Below shows the current JSON representation of the above feature gates and experiment
							overrides. You can modify this JSON and click "Apply (Put)" to update all overrides.
						</Box>
						{!isJsonValid && (
							<Text size="small" color="color.text.danger">
								Invalid JSON format
							</Text>
						)}

						<TextArea
							name="Overrides in JSON format"
							value={jsonInput}
							onChange={onChangeValidation}
							placeholder="Paste JSON data here"
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
								...textAreaStyles,
								borderColor: isJsonValid ? 'initial' : 'red',
							}}
						/>

						<Tooltip
							content="Clear all current overrides and import the provided JSON"
							position="top"
						>
							{(tooltipProps) => (
								<Button
									{...tooltipProps}
									appearance="primary"
									onClick={replaceAll}
									isDisabled={!isJsonValid}
								>
									Apply (Put)
								</Button>
							)}
						</Tooltip>
					</Fragment>
				)}
				<Box xcss={styles.toggleTextarea}>
					<Button onClick={toggleTextarea}>
						{isTextareaCollapsed ? '↓ Expand' : '↑ Collapse'} raw config in JSON
					</Button>
				</Box>
			</Box>

			{fg('use-studio-dynamic-prompt-fg') && (
				<>
					<Box xcss={styles.divider} />
					<Heading size="small" as="h4">
						Dynamic Prompt FG
					</Heading>
					<Box xcss={styles.dynamicPromptFGInputContainer}>
						<DynamicPromptFGInput />
					</Box>
				</>
			)}
			<>
				<Box xcss={styles.divider} />
				<StudioAppSwitcherToggle updateOverrides={updateOverrides} />
			</>
		</Box>
	);
};
